import type { IScannerControls } from "@zxing/browser";
import {
  Activity,
  AlertTriangle,
  CalendarDays,
  Camera,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  Coins,
  Gift,
  History,
  LoaderCircle,
  Megaphone,
  PackageCheck,
  Plus,
  RotateCcw,
  ShieldCheck,
  ScanLine,
  TicketCheck,
  UserRound,
  VideoOff,
  WalletCards,
  Workflow,
  X,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { useEffect, useMemo, useRef, useState, type ReactNode, type SyntheticEvent } from "react";
import {
  type CheckoutLocation,
  type Reward,
  type TenantCampaignChannel,
  type TenantRewardCalendarItem,
  type TenantRewardAutomationInput,
  type TenantRewardAutomationTemplate,
  type TenantRewardFulfillmentInput,
  type TenantManualRewardIssueResult,
  type TenantRewardRedemption,
  createTenantRewardAutomation,
  createTenantRewardCampaign,
  fetchBusinessSubscription,
  fetchTenantRewardAudit,
  fetchTenantRewardAutomations,
  fetchTenantRewardCalendar,
  fetchTenantRewardCampaigns,
  fetchTenantRewardDashboard,
  fetchTenantRewardRedemptions,
  fetchTenantCustomers,
  fulfillTenantRewardRedemption,
  issueTenantReward,
  messageFromError,
  reverseTenantRewardRedemption,
  updateTenantRewardAutomationStatus,
  validateTenantRewardRedemption,
} from "../api";
import { describeCameraFailure, rearCameraConstraints } from "../camera";
import { AppAlert } from "../components/AppAlertDialog";
import {
  SubscriptionUpgradeDialog,
  type SubscriptionUpgradeReason,
} from "../components/SubscriptionUpgradeDialog";
import { limitUpgradeReason } from "../subscription-access";

const calendarKey = ["tenant-reward-calendar"] as const;
const campaignsKey = ["tenant-reward-campaigns"] as const;
const automationsKey = ["tenant-reward-automations"] as const;
const redemptionsKey = ["tenant-reward-redemptions"] as const;
const auditKey = ["tenant-reward-audit"] as const;
const dashboardKey = ["tenant-reward-dashboard"] as const;

const formatNumber = (value: number) => new Intl.NumberFormat("en-US").format(value);
const formatMoney = (minor: number, currency = "NPR") => new Intl.NumberFormat("en-NP", {
  style: "currency",
  currency,
  maximumFractionDigits: 0,
}).format(minor / 100);
const formatDate = (value: string | null, withTime = false) => {
  if (!value) return "Not scheduled";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not scheduled";
  return new Intl.DateTimeFormat("en", withTime
    ? { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }
    : { month: "short", day: "numeric", year: "numeric" }).format(date);
};
const titleCase = (value: string) => value.toLowerCase().replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
const defaultTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kathmandu";

export const localDateTimeToIso = (value: string) => value ? new Date(value).toISOString() : null;

function LoadingState({ label }: { label: string }) {
  return <div className="reward-management-state" role="status"><Activity className="spinner" /><strong>{label}</strong></div>;
}

function ErrorState({ error, retry }: { error: unknown; retry: () => void }) {
  return <><AppAlert message={messageFromError(error)} /><div className="reward-management-state reward-management-state--error"><AlertTriangle /><strong>This section could not be loaded.</strong><button className="button button--outline button--compact" type="button" onClick={retry}>Try again</button></div></>;
}

function EmptyState({ icon, title, detail, action }: { icon: ReactNode; title: string; detail: string; action?: ReactNode }) {
  return <div className="reward-management-state reward-management-state--empty"><span>{icon}</span><strong>{title}</strong><p>{detail}</p>{action}</div>;
}

function StatusBadge({ status }: { status: string }) {
  const token = status.toLowerCase().replace(/[^a-z0-9_-]/g, "-");
  return <span className={`management-status management-status--${token}`}>{titleCase(status)}</span>;
}

const rewardCalendarStatuses = new Set(["ACTIVE", "SCHEDULED"]);
const rewardCalendarColors = ["crimson", "gold", "navy", "magenta", "coral", "teal"] as const;

const startOfDay = (value: Date) => new Date(value.getFullYear(), value.getMonth(), value.getDate());
const endOfDay = (value: Date) => new Date(value.getFullYear(), value.getMonth(), value.getDate() + 1);
const itemOccursOn = (item: TenantRewardCalendarItem, date: Date) => {
  const dayStart = startOfDay(date).getTime();
  const dayEnd = endOfDay(date).getTime();
  const itemStart = item.startsAt ? new Date(item.startsAt).getTime() : Number.NEGATIVE_INFINITY;
  const itemEnd = item.endsAt ? new Date(item.endsAt).getTime() : Number.POSITIVE_INFINITY;
  return itemStart < dayEnd && itemEnd >= dayStart;
};
const itemOverlapsMonth = (item: TenantRewardCalendarItem, month: Date) => {
  const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
  const nextMonth = new Date(month.getFullYear(), month.getMonth() + 1, 1);
  const itemStart = item.startsAt ? new Date(item.startsAt).getTime() : Number.NEGATIVE_INFINITY;
  const itemEnd = item.endsAt ? new Date(item.endsAt).getTime() : Number.POSITIVE_INFINITY;
  return itemStart < nextMonth.getTime() && itemEnd >= firstDay.getTime();
};
const monthGridDays = (month: Date) => {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const gridStart = new Date(month.getFullYear(), month.getMonth(), 1 - first.getDay());
  return Array.from({ length: 42 }, (_, index) =>
    new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + index));
};
const dateKey = (value: Date) =>
  `${String(value.getFullYear())}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
const rewardAvailabilityLabel = (item: TenantRewardCalendarItem) => {
  if (!item.startsAt && !item.endsAt) return "Available without fixed dates";
  if (!item.startsAt) return `Available through ${formatDate(item.endsAt)}`;
  if (!item.endsAt) return `From ${formatDate(item.startsAt)} onward`;
  return `${formatDate(item.startsAt)} – ${formatDate(item.endsAt)}`;
};

export function RewardCalendarView() {
  const calendarQuery = useQuery({ queryKey: calendarKey, queryFn: fetchTenantRewardCalendar });
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const availableRewards = useMemo(
    () => (calendarQuery.data ?? []).filter((item) =>
      item.itemType.toUpperCase() === "REWARD" && rewardCalendarStatuses.has(item.status.toUpperCase())),
    [calendarQuery.data],
  );
  const monthRewards = useMemo(
    () => availableRewards.filter((item) => itemOverlapsMonth(item, visibleMonth)),
    [availableRewards, visibleMonth],
  );
  const calendarDays = useMemo(() => monthGridDays(visibleMonth), [visibleMonth]);
  const colorByReward = useMemo(() => {
    const stableOrder = [...availableRewards].sort((left, right) => left.publicId.localeCompare(right.publicId));
    return new Map(stableOrder.map((item, index) => [item.publicId, rewardCalendarColors[index % rewardCalendarColors.length]]));
  }, [availableRewards]);
  const todayKey = dateKey(new Date());
  const monthLabel = new Intl.DateTimeFormat("en", { month: "long", year: "numeric" }).format(visibleMonth);
  const moveMonth = (offset: number) => {
    setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() + offset, 1));
  };
  const returnToCurrentMonth = () => {
    const today = new Date();
    setVisibleMonth(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  return <section className="portal-panel reward-management-panel">
    <div className="panel-heading"><div><p className="eyebrow">Monthly availability</p><h2>Reward calendar</h2><p>See when active and scheduled offers are available, with a matching color for every reward.</p></div><button className="button button--quiet button--compact" type="button" onClick={() => { void calendarQuery.refetch(); }}>Refresh</button></div>
    {calendarQuery.isLoading ? <LoadingState label="Loading calendar" /> : calendarQuery.isError ? <ErrorState error={calendarQuery.error} retry={() => { void calendarQuery.refetch(); }} /> : <div className="monthly-reward-layout">
      <div className="monthly-calendar-shell">
        <div className="monthly-calendar-toolbar">
          <div><button type="button" aria-label="Previous month" onClick={() => { moveMonth(-1); }}><ChevronLeft /></button><button type="button" aria-label="Next month" onClick={() => { moveMonth(1); }}><ChevronRight /></button></div>
          <h3 aria-live="polite">{monthLabel}</h3>
          <button className="button button--outline button--compact" type="button" onClick={returnToCurrentMonth}>Today</button>
        </div>
        <div className="monthly-calendar-table" role="grid" aria-label={`${monthLabel} reward availability`}>
          <div className="monthly-calendar-weekdays" role="row">{[["Sunday", "Sun"], ["Monday", "Mon"], ["Tuesday", "Tue"], ["Wednesday", "Wed"], ["Thursday", "Thu"], ["Friday", "Fri"], ["Saturday", "Sat"]].map(([label, shortLabel]) => <span aria-label={label} key={label} role="columnheader">{shortLabel}</span>)}</div>
          <div className="monthly-calendar-grid">
            {Array.from({ length: 6 }, (_, weekIndex) => <div className="monthly-calendar-week" key={`week-${String(weekIndex)}`} role="row">
              {calendarDays.slice(weekIndex * 7, weekIndex * 7 + 7).map((day) => {
                const key = dateKey(day);
                const inMonth = day.getMonth() === visibleMonth.getMonth();
                const offers = inMonth ? monthRewards.filter((item) => itemOccursOn(item, day)) : [];
                return <div className={`monthly-calendar-day${inMonth ? "" : " is-outside"}${key === todayKey ? " is-today" : ""}`} key={key} role="gridcell" aria-label={`${new Intl.DateTimeFormat("en", { weekday: "long", month: "long", day: "numeric", year: "numeric" }).format(day)}, ${String(offers.length)} ${offers.length === 1 ? "offer" : "offers"}`}>
                  <time aria-current={key === todayKey ? "date" : undefined} dateTime={key}>{day.getDate()}</time>
                  <div className="monthly-calendar-day__offers">{offers.slice(0, 3).map((item) => <span className={`monthly-calendar-offer calendar-offer--${colorByReward.get(item.publicId) ?? "crimson"}`} key={item.publicId} title={`${item.title} · ${titleCase(item.status)}`}>{item.title}</span>)}{offers.length > 3 && <small>+{String(offers.length - 3)} more</small>}</div>
                </div>;
              })}
            </div>)}
          </div>
        </div>
      </div>
      <aside className="calendar-offer-list" aria-label={`Available rewards for ${monthLabel}`}>
        <div className="calendar-offer-list__heading"><div><p className="eyebrow">Available rewards</p><h3>{String(monthRewards.length)} {monthRewards.length === 1 ? "offer" : "offers"} this month</h3></div><Gift /></div>
        {monthRewards.length ? <div className="calendar-offer-list__items">{monthRewards.map((item) => <article key={item.publicId}>
          <span className={`calendar-offer-list__swatch calendar-offer--${colorByReward.get(item.publicId) ?? "crimson"}`} />
          <div><strong>{item.title}</strong><p>{rewardAvailabilityLabel(item)}</p><StatusBadge status={item.status} /></div>
        </article>)}</div> : <EmptyState icon={<CalendarDays />} title="No offers this month" detail="Use the month controls to find scheduled rewards, or activate an offer in the catalog." />}
      </aside>
    </div>}
  </section>;
}

const campaignObjectives = [
  "ACQUIRE_CUSTOMER",
  "INCREASE_FREQUENCY",
  "INCREASE_SPEND",
  "RETAIN_CUSTOMER",
  "WIN_BACK",
  "CELEBRATE_RECOGNIZE",
  "SERVICE_RECOVERY",
  "PROMOTE_PRODUCT",
  "ENCOURAGE_REFERRAL",
  "CUSTOM",
] as const;
const campaignChannels: TenantCampaignChannel[] = ["IN_APP", "EMAIL", "PUSH", "SMS"];

function CampaignForm({ rewards, initialTimezone, canManageBudget, close, saved }: { rewards: Reward[]; initialTimezone: string; canManageBudget: boolean; close: () => void; saved: () => Promise<void> }) {
  const [name, setName] = useState("");
  const [objective, setObjective] = useState<(typeof campaignObjectives)[number]>("RETAIN_CUSTOMER");
  const [rewardPublicId, setRewardPublicId] = useState("");
  const [budget, setBudget] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [timezone, setTimezone] = useState(initialTimezone);
  const [error, setError] = useState("");
  const mutation = useMutation({ mutationFn: createTenantRewardCampaign });
  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    if (startsAt && endsAt && new Date(endsAt) <= new Date(startsAt)) { setError("Campaign end must be after its start."); return; }
    try {
      await mutation.mutateAsync({
        name: name.trim(),
        objective,
        rewardPublicId: rewardPublicId || null,
        audience: { type: "ALL_ACTIVE_MEMBERS" },
        channelScope: ["IN_APP"],
        budgetMinor: canManageBudget && budget ? Math.max(1, Math.round(Number(budget) * 100)) : null,
        startsAt: localDateTimeToIso(startsAt),
        endsAt: localDateTimeToIso(endsAt),
        timezone: timezone.trim(),
      });
      await saved();
      close();
    } catch (caught) { setError(messageFromError(caught)); }
  };
  return <form className="management-create-form" onSubmit={(event) => { void submit(event); }}>
    <div className="management-create-form__heading"><div><p className="eyebrow">New draft</p><h3>Create campaign</h3><p>Campaigns issue rewards in Mero Perks. Consent-aware email, push, and SMS delivery will be enabled separately.</p></div><button className="icon-control" type="button" aria-label="Close campaign form" onClick={close}><X /></button></div>
    {error && <AppAlert message={error} onDismiss={() => { setError(""); }} />}
    <div className="management-form-grid">
      <label><span>Campaign name</span><input required maxLength={160} value={name} onChange={(event) => { setName(event.target.value); }} placeholder="Holiday return visit" /></label>
      <label><span>Objective</span><select value={objective} onChange={(event) => { setObjective(event.target.value as (typeof campaignObjectives)[number]); }}>{campaignObjectives.map((item) => <option key={item} value={item}>{titleCase(item)}</option>)}</select></label>
      <label><span>Linked reward <small>(optional)</small></span><select value={rewardPublicId} onChange={(event) => { setRewardPublicId(event.target.value); }}><option value="">No linked reward</option>{rewards.map((reward) => <option key={reward.publicId} value={reward.publicId}>{reward.title}</option>)}</select></label>
      {canManageBudget && <label><span>Budget (NPR) <small>(optional)</small></span><input min="0.01" step="0.01" type="number" value={budget} onChange={(event) => { setBudget(event.target.value); }} placeholder="50000" /></label>}
      <label><span>Starts <small>(optional)</small></span><input type="datetime-local" value={startsAt} onChange={(event) => { setStartsAt(event.target.value); }} /></label>
      <label><span>Ends <small>(optional)</small></span><input type="datetime-local" value={endsAt} onChange={(event) => { setEndsAt(event.target.value); }} /></label>
      <label className="management-field--wide"><span>Timezone</span><input required maxLength={80} value={timezone} onChange={(event) => { setTimezone(event.target.value); }} /></label>
      <fieldset className="management-field--wide"><legend>Campaign delivery</legend><p>In-app reward issuance only. Outbound messages are not sent by campaign execution.</p></fieldset>
    </div>
    <div className="management-form-actions"><button className="button button--quiet" type="button" onClick={close}>Cancel</button><button className="button button--primary" disabled={mutation.isPending} type="submit">{mutation.isPending ? "Creating…" : "Create draft campaign"}</button></div>
  </form>;
}

export function RewardCampaignsView({ rewards, timezone = defaultTimezone(), canManageBudget }: { rewards: Reward[]; timezone?: string; canManageBudget: boolean }) {
  const queryClient = useQueryClient();
  const campaignsQuery = useQuery({ queryKey: campaignsKey, queryFn: fetchTenantRewardCampaigns });
  const subscription = useQuery({ queryKey: ["tenant-subscription"], queryFn: fetchBusinessSubscription });
  const [creating, setCreating] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState<SubscriptionUpgradeReason | null>(null);
  const beginCampaign = () => {
    const limit = subscription.data?.accessPlan.limits.campaigns;
    const used = subscription.data?.usage.campaigns ?? campaignsQuery.data?.length ?? 0;
    if (subscription.data && limit !== null && limit !== undefined && used >= limit) {
      setUpgradeReason(limitUpgradeReason(subscription.data, "active campaigns", limit));
      return;
    }
    setCreating(true);
  };
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: campaignsKey }),
      queryClient.invalidateQueries({ queryKey: calendarKey }),
      queryClient.invalidateQueries({ queryKey: auditKey }),
      queryClient.invalidateQueries({ queryKey: ["tenant-subscription"] }),
    ]);
  };
  return <section className="portal-panel reward-management-panel">
    {upgradeReason && <SubscriptionUpgradeDialog reason={upgradeReason} close={() => { setUpgradeReason(null); }} />}
    <div className="panel-heading"><div><p className="eyebrow">Audience orchestration</p><h2>Campaigns</h2><p>Connect a reward, objective, in-app issuance schedule, and budget in one campaign record.</p></div><button className="button button--primary button--compact" type="button" onClick={beginCampaign}><Plus /> New campaign</button></div>
    {creating && <CampaignForm rewards={rewards} initialTimezone={timezone} canManageBudget={canManageBudget} close={() => { setCreating(false); }} saved={refresh} />}
    {campaignsQuery.isLoading ? <LoadingState label="Loading campaigns" /> : campaignsQuery.isError ? <ErrorState error={campaignsQuery.error} retry={() => { void campaignsQuery.refetch(); }} /> : campaignsQuery.data?.length ? <div className="management-card-grid">{campaignsQuery.data.map((campaign) => {
      const conversion = campaign.issuedCount ? (campaign.redeemedCount / campaign.issuedCount) * 100 : 0;
      return <article className="management-card" key={campaign.publicId}>
        <div className="management-card__heading"><span><Megaphone /></span><div><small>{titleCase(campaign.objective)}</small><h3>{campaign.name}</h3></div><StatusBadge status={campaign.status} /></div>
        <p>{campaign.reward ? `Linked to ${campaign.reward.name}` : "No reward linked yet"}{campaign.segment ? ` · ${campaign.segment.name}` : ""}</p>
        <div className="management-pills">{campaign.channelScope.map((channel) => <span key={channel}>{titleCase(channel)}</span>)}</div>
        <dl><div><dt>Schedule</dt><dd>{formatDate(campaign.startsAt)} – {campaign.endsAt ? formatDate(campaign.endsAt) : "Open end"}</dd></div>{canManageBudget && <div><dt>Budget</dt><dd>{campaign.budgetMinor === null ? "No cap" : formatMoney(campaign.budgetMinor)}</dd></div>}<div><dt>Issued</dt><dd>{formatNumber(campaign.issuedCount)}</dd></div><div><dt>Redeemed</dt><dd>{formatNumber(campaign.redeemedCount)} · {conversion.toFixed(1)}%</dd></div></dl>
      </article>;
    })}</div> : <EmptyState icon={<Megaphone />} title="No campaigns yet" detail="Create a campaign draft to coordinate audience, in-app issuance, timing, and budget." action={<button className="button button--outline" type="button" onClick={beginCampaign}>Create campaign</button>} />}
  </section>;
}

const automationTemplates: Array<[TenantRewardAutomationTemplate, string]> = [
  ["WELCOME", "Welcome"],
  ["FIRST_PURCHASE", "First purchase"],
  ["BIRTHDAY", "Birthday"],
  ["MEMBERSHIP_ANNIVERSARY", "Membership anniversary"],
  ["MILESTONE", "Milestone"],
  ["INACTIVITY", "Inactivity / win-back"],
  ["REWARD_EXPIRING", "Reward expiring"],
  ["POINTS_EXPIRING", "Points expiring"],
  ["TIER_STATUS", "Tier status"],
  ["REFERRAL_QUALIFIED", "Qualified referral"],
  ["SPECIAL_EVENT", "Special event"],
  ["SERVICE_RECOVERY", "Service recovery"],
];

type AutomationCadence = "DAILY" | "HOURLY";
type AutomationCalendarTiming = "EXACT_DATE" | "DAYS_BEFORE" | "BIRTHDAY_WEEK" | "BIRTHDAY_MONTH" | "FIRST_DAY_OF_BIRTHDAY_MONTH";
type AutomationLeapDayBehavior = "FEB_28" | "MAR_01" | "EXACT_DATE_ONLY";
type AutomationMilestoneMetric = "PURCHASE_COUNT" | "VISIT_COUNT" | "SPEND_MINOR";

const executableAutomationTemplates: ReadonlySet<TenantRewardAutomationTemplate> = new Set([
  "WELCOME",
  "FIRST_PURCHASE",
  "BIRTHDAY",
  "MEMBERSHIP_ANNIVERSARY",
  "MILESTONE",
  "INACTIVITY",
  "REWARD_EXPIRING",
  "POINTS_EXPIRING",
  "TIER_STATUS",
  "SPECIAL_EVENT",
]);

const historicalAutomationTemplates: ReadonlySet<TenantRewardAutomationTemplate> = new Set([
  "WELCOME",
  "FIRST_PURCHASE",
  "MILESTONE",
  "TIER_STATUS",
]);

const draftOnlyAutomationDetails: Partial<Record<TenantRewardAutomationTemplate, { actionType: string; message: string }>> = {
  REFERRAL_QUALIFIED: {
    actionType: "QUALIFIED_REFERRAL_EVENT_REQUIRED",
    message: "Referral rewards require a verified referral qualification event. The lifecycle scheduler cannot run this definition.",
  },
  SERVICE_RECOVERY: {
    actionType: "MANUAL_ONLY",
    message: "Service-recovery rewards require a deliberate manual issue. This definition cannot be scheduled or activated.",
  },
};

export interface GuidedAutomationConfigurationInput {
  templateCode: TenantRewardAutomationTemplate;
  requestedStatus: TenantRewardAutomationInput["status"];
  cadence: AutomationCadence;
  reason: string;
  channels: TenantCampaignChannel[];
  requireFirstPurchase: boolean;
  includeHistorical: boolean;
  calendarTiming: AutomationCalendarTiming;
  daysBefore: string | number;
  expiryDaysBefore: string | number;
  leapDayBehavior: AutomationLeapDayBehavior;
  milestoneMetric: AutomationMilestoneMetric;
  milestoneThreshold: string | number;
  visitCooldownHours: string | number;
  inactivityDays: string | number;
  tierCode: string;
  eventDate: string;
  repeatAnnually: boolean;
}

export interface GuidedAutomationConfiguration {
  trigger: Record<string, unknown>;
  action: Record<string, unknown>;
  status: TenantRewardAutomationInput["status"];
  executable: boolean;
}

export const isExecutableAutomationTemplate = (templateCode: TenantRewardAutomationTemplate) => executableAutomationTemplates.has(templateCode);

const isExpiryReminderTemplate = (templateCode: TenantRewardAutomationTemplate) =>
  templateCode === "REWARD_EXPIRING" || templateCode === "POINTS_EXPIRING";

const requiredSafeInteger = (value: string | number, label: string, minimum: number, maximum = Number.MAX_SAFE_INTEGER) => {
  const number = typeof value === "string" && value.trim() === "" ? Number.NaN : Number(value);
  if (!Number.isSafeInteger(number) || number < minimum || number > maximum) {
    throw new Error(`${label} must be a whole number from ${String(minimum)} to ${String(maximum)}.`);
  }
  return number;
};

const isValidCalendarDate = (value: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const [, yearValue, monthValue, dayValue] = match;
  const year = Number(yearValue);
  const month = Number(monthValue);
  const day = Number(dayValue);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day;
};

export function buildGuidedAutomationConfiguration(input: GuidedAutomationConfigurationInput): GuidedAutomationConfiguration {
  const reason = input.reason.trim();
  if (reason.length < 2) throw new Error("Issue reason must contain at least two characters.");
  const channels = [...new Set(input.channels)];
  if (!channels.length) throw new Error("Choose at least one delivery channel.");

  if (!isExecutableAutomationTemplate(input.templateCode)) {
    const detail = draftOnlyAutomationDetails[input.templateCode];
    return {
      trigger: { executionMode: "DRAFT_ONLY" },
      action: { type: detail?.actionType ?? "DRAFT_ONLY", reason, channels },
      status: "DRAFT",
      executable: false,
    };
  }

  let trigger: Record<string, unknown>;
  switch (input.templateCode) {
    case "WELCOME":
      trigger = {
        cadence: input.cadence,
        requireFirstPurchase: input.requireFirstPurchase,
        includeHistorical: input.includeHistorical,
      };
      break;
    case "FIRST_PURCHASE":
      trigger = { cadence: input.cadence, includeHistorical: input.includeHistorical };
      break;
    case "BIRTHDAY":
    case "MEMBERSHIP_ANNIVERSARY":
      trigger = {
        cadence: input.cadence,
        timing: input.calendarTiming,
        daysBefore: input.calendarTiming === "DAYS_BEFORE"
          ? requiredSafeInteger(input.daysBefore, "Days before", 0, 365)
          : 0,
        leapDayBehavior: input.leapDayBehavior,
      };
      break;
    case "MILESTONE": {
      const displayedThreshold = Number(input.milestoneThreshold);
      if (!Number.isFinite(displayedThreshold) || displayedThreshold <= 0) {
        throw new Error("Milestone threshold must be greater than zero.");
      }
      const threshold = input.milestoneMetric === "SPEND_MINOR"
        ? Math.round(displayedThreshold * 100)
        : requiredSafeInteger(input.milestoneThreshold, "Milestone threshold", 1);
      if (!Number.isSafeInteger(threshold) || threshold < 1) {
        throw new Error("Spend threshold is outside the supported range.");
      }
      trigger = {
        cadence: input.cadence,
        metric: input.milestoneMetric,
        threshold,
        ...(input.milestoneMetric === "VISIT_COUNT"
          ? { visitCooldownHours: requiredSafeInteger(input.visitCooldownHours, "Visit cooldown", 1, 720) }
          : {}),
        includeHistorical: input.includeHistorical,
      };
      break;
    }
    case "INACTIVITY":
      trigger = {
        cadence: input.cadence,
        inactivityDays: requiredSafeInteger(input.inactivityDays, "Inactivity period", 1, 3_650),
      };
      break;
    case "REWARD_EXPIRING":
    case "POINTS_EXPIRING":
      trigger = {
        cadence: input.cadence,
        daysBefore: requiredSafeInteger(input.expiryDaysBefore, "Reminder lead time", 1, 365),
      };
      break;
    case "TIER_STATUS":
      if (!/^MERO(?:_[A-Z][A-Z0-9_]*)?$/.test(input.tierCode)) throw new Error("Choose a valid Mero tier.");
      trigger = { cadence: input.cadence, tierCode: input.tierCode, includeHistorical: input.includeHistorical };
      break;
    case "SPECIAL_EVENT":
      if (!isValidCalendarDate(input.eventDate)) {
        throw new Error("Choose a valid special-event date.");
      }
      trigger = { cadence: input.cadence, eventDate: input.eventDate, repeatAnnually: input.repeatAnnually };
      break;
    default:
      throw new Error("This automation template is not executable.");
  }

  return {
    trigger,
    action: { type: isExpiryReminderTemplate(input.templateCode) ? "SEND_REMINDER" : "ISSUE_REWARD", reason, channels },
    status: input.requestedStatus,
    executable: true,
  };
}

function AutomationForm({ rewards, initialTimezone, close, saved }: { rewards: Reward[]; initialTimezone: string; close: () => void; saved: () => Promise<void> }) {
  const [name, setName] = useState("");
  const [templateCode, setTemplateCode] = useState<TenantRewardAutomationTemplate>("WELCOME");
  const [rewardPublicId, setRewardPublicId] = useState("");
  const [status, setStatus] = useState<TenantRewardAutomationInput["status"]>("DRAFT");
  const [frequencyCapDays, setFrequencyCapDays] = useState("30");
  const [nextRunAt, setNextRunAt] = useState("");
  const [timezone, setTimezone] = useState(initialTimezone);
  const [cadence, setCadence] = useState<AutomationCadence>("DAILY");
  const [reason, setReason] = useState("");
  const [channels, setChannels] = useState<TenantCampaignChannel[]>(["IN_APP"]);
  const [requireFirstPurchase, setRequireFirstPurchase] = useState(false);
  const [includeHistorical, setIncludeHistorical] = useState(false);
  const [calendarTiming, setCalendarTiming] = useState<AutomationCalendarTiming>("EXACT_DATE");
  const [daysBefore, setDaysBefore] = useState("7");
  const [expiryDaysBefore, setExpiryDaysBefore] = useState("7");
  const [leapDayBehavior, setLeapDayBehavior] = useState<AutomationLeapDayBehavior>("FEB_28");
  const [milestoneMetric, setMilestoneMetric] = useState<AutomationMilestoneMetric>("PURCHASE_COUNT");
  const [milestoneThreshold, setMilestoneThreshold] = useState("5");
  const [visitCooldownHours, setVisitCooldownHours] = useState("24");
  const [inactivityDays, setInactivityDays] = useState("30");
  const [tierCode, setTierCode] = useState("MERO_PLUS");
  const [eventDate, setEventDate] = useState("");
  const [repeatAnnually, setRepeatAnnually] = useState(false);
  const [error, setError] = useState("");
  const mutation = useMutation({ mutationFn: createTenantRewardAutomation });
  const executable = isExecutableAutomationTemplate(templateCode);
  const expiryReminder = isExpiryReminderTemplate(templateCode);
  const issuesReward = executable && !expiryReminder;
  const supportsHistorical = historicalAutomationTemplates.has(templateCode);
  const draftOnlyDetail = draftOnlyAutomationDetails[templateCode];
  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    try {
      const configuration = buildGuidedAutomationConfiguration({
        templateCode,
        requestedStatus: status,
        cadence,
        reason,
        channels,
        requireFirstPurchase,
        includeHistorical,
        calendarTiming,
        daysBefore,
        expiryDaysBefore,
        leapDayBehavior,
        milestoneMetric,
        milestoneThreshold,
        visitCooldownHours,
        inactivityDays,
        tierCode,
        eventDate,
        repeatAnnually,
      });
      if (issuesReward && configuration.status === "ACTIVE" && !rewardPublicId) {
        throw new Error("Choose a reward before activating this automation.");
      }
      await mutation.mutateAsync({
        name: name.trim(),
        rewardPublicId: issuesReward ? rewardPublicId || null : null,
        templateCode,
        trigger: configuration.trigger,
        action: configuration.action,
        timezone: timezone.trim(),
        frequencyCapDays: configuration.executable && frequencyCapDays
          ? requiredSafeInteger(frequencyCapDays, "Frequency cap", 1, 3_650)
          : null,
        status: configuration.status,
        nextRunAt: configuration.executable ? localDateTimeToIso(nextRunAt) : null,
      });
      await saved();
      close();
    } catch (caught) { setError(messageFromError(caught)); }
  };
  return <form className="management-create-form" onSubmit={(event) => { void submit(event); }}>
    <div className="management-create-form__heading"><div><p className="eyebrow">New workflow</p><h3>Create automation</h3><p>Choose the audience event, delivery timing, and customer message. Mero Perks builds the executable configuration for you.</p></div><button className="icon-control" type="button" aria-label="Close automation form" onClick={close}><X /></button></div>
    {error && <AppAlert message={error} onDismiss={() => { setError(""); }} />}
    <div className="management-form-grid">
      <label><span>Automation name</span><input required maxLength={160} value={name} onChange={(event) => { setName(event.target.value); }} placeholder="Birthday reward delivery" /></label>
      <label><span>Customer moment</span><select aria-describedby={!executable ? "automation-mode-note" : undefined} value={templateCode} onChange={(event) => { const selected = event.target.value as TenantRewardAutomationTemplate; setTemplateCode(selected); setIncludeHistorical(false); if (!isExecutableAutomationTemplate(selected)) { setStatus("DRAFT"); setNextRunAt(""); } }}>{automationTemplates.map(([value, label]) => <option key={value} value={value}>{label}{isExecutableAutomationTemplate(value) ? "" : " (draft only)"}</option>)}</select></label>
      {!executable && <div className="automation-mode-note management-field--wide" id="automation-mode-note" role="status"><AlertTriangle aria-hidden="true" /><div><strong>Draft-only workflow</strong><span>{draftOnlyDetail?.message}</span></div></div>}
      {issuesReward && <label><span>Linked reward <small>(required when active)</small></span><select value={rewardPublicId} onChange={(event) => { setRewardPublicId(event.target.value); }}><option value="">Choose later</option>{rewards.map((reward) => <option key={reward.publicId} value={reward.publicId}>{reward.title}</option>)}</select></label>}
      <label><span>Initial status</span><select aria-describedby={!executable ? "automation-mode-note" : undefined} disabled={!executable} value={executable ? status : "DRAFT"} onChange={(event) => { setStatus(event.target.value as TenantRewardAutomationInput["status"]); }}><option value="DRAFT">Draft</option>{executable && <option value="ACTIVE">Active</option>}{executable && <option value="PAUSED">Paused</option>}</select></label>
      {executable && <label><span>Evaluation cadence</span><select value={cadence} onChange={(event) => { setCadence(event.target.value as AutomationCadence); }}><option value="DAILY">Daily</option><option value="HOURLY">Hourly</option></select></label>}
      {executable && <label><span>Frequency cap (days) <small>(optional)</small></span><input min="1" max="3650" step="1" type="number" value={frequencyCapDays} onChange={(event) => { setFrequencyCapDays(event.target.value); }} /></label>}
      {executable && <label><span>First eligible run <small>(optional)</small></span><input type="datetime-local" value={nextRunAt} onChange={(event) => { setNextRunAt(event.target.value); }} /></label>}
      <label className="management-field--wide"><span>Timezone</span><input required maxLength={80} value={timezone} onChange={(event) => { setTimezone(event.target.value); }} /></label>
      {templateCode === "WELCOME" && <fieldset><legend>Welcome eligibility</legend><div className="management-check-grid management-check-grid--compact"><label><input type="checkbox" checked={requireFirstPurchase} onChange={(event) => { setRequireFirstPurchase(event.target.checked); }} /><span>Require a first purchase</span></label></div></fieldset>}
      {(templateCode === "BIRTHDAY" || templateCode === "MEMBERSHIP_ANNIVERSARY") && <>
        <label><span>Delivery timing</span><select value={calendarTiming} onChange={(event) => { setCalendarTiming(event.target.value as AutomationCalendarTiming); }}><option value="EXACT_DATE">On the event date</option><option value="DAYS_BEFORE">Days before the event</option><option value="BIRTHDAY_WEEK">During the event week</option><option value="BIRTHDAY_MONTH">During the event month</option><option value="FIRST_DAY_OF_BIRTHDAY_MONTH">First day of the event month</option></select></label>
        {calendarTiming === "DAYS_BEFORE" && <label><span>Days before</span><input required min="0" max="365" step="1" type="number" value={daysBefore} onChange={(event) => { setDaysBefore(event.target.value); }} /></label>}
        <label><span>February 29 handling</span><select value={leapDayBehavior} onChange={(event) => { setLeapDayBehavior(event.target.value as AutomationLeapDayBehavior); }}><option value="FEB_28">Observe on February 28</option><option value="MAR_01">Observe on March 1</option><option value="EXACT_DATE_ONLY">Only in leap years</option></select></label>
      </>}
      {templateCode === "MILESTONE" && <>
        <label><span>Milestone measure</span><select value={milestoneMetric} onChange={(event) => { setMilestoneMetric(event.target.value as AutomationMilestoneMetric); }}><option value="PURCHASE_COUNT">Confirmed purchases</option><option value="VISIT_COUNT">Verified visits</option><option value="SPEND_MINOR">Confirmed spend</option></select></label>
        <label><span>{milestoneMetric === "SPEND_MINOR" ? "Spend threshold (NPR)" : milestoneMetric === "VISIT_COUNT" ? "Visit threshold" : "Purchase threshold"}</span><input required min={milestoneMetric === "SPEND_MINOR" ? "0.01" : "1"} step={milestoneMetric === "SPEND_MINOR" ? "0.01" : "1"} type="number" value={milestoneThreshold} onChange={(event) => { setMilestoneThreshold(event.target.value); }} /></label>
        {milestoneMetric === "VISIT_COUNT" && <label><span>Visit cooldown (hours)</span><input required min="1" max="720" step="1" type="number" value={visitCooldownHours} onChange={(event) => { setVisitCooldownHours(event.target.value); }} /></label>}
      </>}
      {templateCode === "INACTIVITY" && <label><span>Inactive for (days)</span><input required min="1" max="3650" step="1" type="number" value={inactivityDays} onChange={(event) => { setInactivityDays(event.target.value); }} /></label>}
      {expiryReminder && <label><span>Remind before expiration (days)</span><input required min="1" max="365" step="1" type="number" value={expiryDaysBefore} onChange={(event) => { setExpiryDaysBefore(event.target.value); }} /></label>}
      {templateCode === "TIER_STATUS" && <label><span>Mero tier reached</span><select value={tierCode} onChange={(event) => { setTierCode(event.target.value); }}><option value="MERO">Mero</option><option value="MERO_PLUS">Mero Plus</option><option value="MERO_GOLD">Mero Gold</option><option value="MERO_ELITE">Mero Elite</option></select></label>}
      {templateCode === "SPECIAL_EVENT" && <>
        <label><span>Event date</span><input required type="date" value={eventDate} onChange={(event) => { setEventDate(event.target.value); }} /></label>
        <fieldset><legend>Event recurrence</legend><div className="management-check-grid management-check-grid--compact"><label><input type="checkbox" checked={repeatAnnually} onChange={(event) => { setRepeatAnnually(event.target.checked); }} /><span>Repeat every year</span></label></div></fieldset>
      </>}
      {supportsHistorical && <fieldset aria-describedby={includeHistorical ? "historical-issuance-warning" : undefined}><legend>Eligible member history</legend><div className="management-check-grid management-check-grid--compact"><label><input type="checkbox" checked={includeHistorical} onChange={(event) => { setIncludeHistorical(event.target.checked); }} /><span>Include members who qualified before activation</span></label></div>{includeHistorical && <div className="automation-history-warning" id="historical-issuance-warning" role="status"><AlertTriangle aria-hidden="true" /><span>This can issue the reward to every previously qualifying member on the first run. Review the reward inventory, liability, and audience before activation.</span></div>}</fieldset>}
      <label className="management-field--wide"><span>{expiryReminder ? "Reminder message" : executable ? "Reward issue reason" : "Workflow reason"}</span><textarea required minLength={2} maxLength={1000} rows={3} value={reason} onChange={(event) => { setReason(event.target.value); }} placeholder={expiryReminder ? "Let the member know when their reward or points expire." : executable ? "Thank the member for reaching this moment." : "Describe the intended customer communication or manual action."} /></label>
      <fieldset><legend>Delivery channels</legend><div className="management-check-grid">{campaignChannels.map((channel) => <label key={channel}><input type="checkbox" checked={channels.includes(channel)} onChange={() => { setChannels((current) => current.includes(channel) ? current.filter((item) => item !== channel) : [...current, channel]); }} /><span>{titleCase(channel)}</span></label>)}</div></fieldset>
    </div>
    <div className="management-form-actions"><button className="button button--quiet" type="button" onClick={close}>Cancel</button><button className="button button--primary" disabled={mutation.isPending} type="submit">{mutation.isPending ? "Creating…" : executable ? "Create automation" : "Save draft definition"}</button></div>
  </form>;
}

const jsonSummary = (value: Record<string, unknown>) => {
  const entries = Object.entries(value);
  if (!entries.length) return "No configuration values";
  return entries.slice(0, 3).map(([key, item]) => {
    const detail = typeof item === "string" || typeof item === "number" || typeof item === "boolean"
      ? String(item)
      : item === null
        ? "null"
        : "configured";
    return `${titleCase(key)}: ${detail}`;
  }).join(" · ");
};

export function RewardAutomationsView({ rewards, timezone = defaultTimezone() }: { rewards: Reward[]; timezone?: string }) {
  const queryClient = useQueryClient();
  const automationsQuery = useQuery({ queryKey: automationsKey, queryFn: fetchTenantRewardAutomations });
  const [creating, setCreating] = useState(false);
  const [lifecycleError, setLifecycleError] = useState("");
  const refresh = async () => {
    await Promise.all([queryClient.invalidateQueries({ queryKey: automationsKey }), queryClient.invalidateQueries({ queryKey: calendarKey }), queryClient.invalidateQueries({ queryKey: auditKey })]);
  };
  const lifecycleMutation = useMutation({
    mutationFn: ({ publicId, status }: { publicId: string; status: "ACTIVE" | "PAUSED" | "ARCHIVED" }) =>
      updateTenantRewardAutomationStatus(publicId, status),
    onSuccess: refresh,
    onError: (error) => { setLifecycleError(messageFromError(error)); },
  });
  const changeStatus = (publicId: string, status: "ACTIVE" | "PAUSED" | "ARCHIVED") => {
    setLifecycleError("");
    lifecycleMutation.mutate({ publicId, status });
  };
  return <section className="portal-panel reward-management-panel">
    <div className="panel-heading"><div><p className="eyebrow">Lifecycle engagement</p><h2>Automations</h2><p>Review scheduled Business Owner workflows and create API-backed trigger/action configurations.</p></div><button className="button button--primary button--compact" type="button" onClick={() => { setCreating(true); }}><Plus /> New automation</button></div>
    {lifecycleError && <AppAlert message={lifecycleError} onDismiss={() => { setLifecycleError(""); }} />}
    {creating && <AutomationForm rewards={rewards} initialTimezone={timezone} close={() => { setCreating(false); }} saved={refresh} />}
    {automationsQuery.isLoading ? <LoadingState label="Loading automations" /> : automationsQuery.isError ? <ErrorState error={automationsQuery.error} retry={() => { void automationsQuery.refetch(); }} /> : automationsQuery.data?.length ? <div className="management-card-grid">{automationsQuery.data.map((automation) => <article className="management-card" key={automation.publicId}>
      <div className="management-card__heading"><span><Workflow /></span><div><small>{titleCase(automation.templateCode)}</small><h3>{automation.name}</h3></div><StatusBadge status={automation.status} /></div>
      <p>{automation.reward ? `Issues ${automation.reward.name}` : "No reward linked"}</p>
      <div className="automation-config"><span><strong>Trigger</strong>{jsonSummary(automation.trigger)}</span><span><strong>Action</strong>{jsonSummary(automation.action)}</span></div>
      <dl><div><dt>Frequency cap</dt><dd>{automation.frequencyCapDays ? `${String(automation.frequencyCapDays)} days` : "Not set"}</dd></div><div><dt>Timezone</dt><dd>{automation.timezone}</dd></div><div><dt>Last run</dt><dd>{formatDate(automation.lastRunAt, true)}</dd></div><div><dt>Next run</dt><dd>{formatDate(automation.nextRunAt, true)}</dd></div></dl>
      {automation.status !== "ARCHIVED" && <div className="management-form-actions">
        {automation.status === "ACTIVE"
          ? <button className="button button--outline button--compact" disabled={lifecycleMutation.isPending} type="button" onClick={() => { changeStatus(automation.publicId, "PAUSED"); }}>Pause</button>
          : isExecutableAutomationTemplate(automation.templateCode as TenantRewardAutomationTemplate) && <button className="button button--primary button--compact" disabled={lifecycleMutation.isPending} type="button" onClick={() => { changeStatus(automation.publicId, "ACTIVE"); }}>Activate</button>}
        <button className="button button--quiet button--compact" disabled={lifecycleMutation.isPending} type="button" onClick={() => { changeStatus(automation.publicId, "ARCHIVED"); }}>Archive</button>
      </div>}
    </article>)}</div> : <EmptyState icon={<Workflow />} title="No automations configured" detail="Create an automation record for welcome, milestone, expiry, or recovery workflows." action={<button className="button button--outline" type="button" onClick={() => { setCreating(true); }}>Create automation</button>} />}
  </section>;
}

function redemptionRequest(
  locationPublicId: string,
  amount: string,
  purchaseReference: string,
  channel: string,
  productCode: string,
  categoryCode: string,
  quantity: string,
  mechanismSelectionId: string,
): TenantRewardFulfillmentInput {
  const hasLineItem = Boolean(productCode.trim() || categoryCode.trim());
  return {
    ...(locationPublicId ? { locationPublicId } : {}),
    ...(amount ? { purchaseAmountMinor: Math.max(0, Math.round(Number(amount) * 100)) } : {}),
    ...(purchaseReference.trim() ? { purchaseReference: purchaseReference.trim() } : {}),
    ...(channel ? { channel } : {}),
    ...(hasLineItem ? { lineItems: [{
      ...(productCode.trim() ? { productCode: productCode.trim() } : {}),
      ...(categoryCode.trim() ? { categoryCode: categoryCode.trim() } : {}),
      quantity: Math.max(1, Math.round(Number(quantity || 1))),
    }] } : {}),
    ...(mechanismSelectionId.trim() ? { mechanismSelectionId: mechanismSelectionId.trim() } : {}),
  };
}

export interface RedemptionCheckoutSummary {
  benefitLabel: string;
  purchaseAmountMinor: number | null;
  appliedValueMinor: number | null;
  customerPaysMinor: number | null;
  checkoutPointsDeducted: 0;
}

export function redemptionCheckoutSummary(redemption: Pick<
  TenantRewardRedemption,
  "rewardType" | "rewardValueMinor" | "currencyCode" | "purchaseAmountMinor" | "appliedValueMinor"
>): RedemptionCheckoutSummary {
  const type = (redemption.rewardType ?? "REWARD").toUpperCase();
  const benefitLabel = type === "PERCENTAGE_DISCOUNT" && redemption.rewardValueMinor !== null
    ? `${(redemption.rewardValueMinor / 100).toFixed(redemption.rewardValueMinor % 100 === 0 ? 0 : 2)}% discount`
    : type === "FIXED_DISCOUNT" && redemption.appliedValueMinor !== null
      ? `${formatMoney(redemption.appliedValueMinor, redemption.currencyCode)} discount`
      : type === "FREE_ITEM"
        ? "Free eligible item"
        : type === "BOGO"
          ? "Buy one, get one benefit"
          : titleCase(type);
  const customerPaysMinor = redemption.purchaseAmountMinor === null
    ? null
    : Math.max(0, redemption.purchaseAmountMinor - (redemption.appliedValueMinor ?? 0));
  return {
    benefitLabel,
    purchaseAmountMinor: redemption.purchaseAmountMinor,
    appliedValueMinor: redemption.appliedValueMinor,
    customerPaysMinor,
    checkoutPointsDeducted: 0,
  };
}

function redemptionStatus(status: string) {
  return status.toLowerCase() === "fulfilled" ? "REDEEMED" : status;
}

function ValidationResult({ redemption, selectedLocationName, busy, fulfill, reset, validationOnly = false }: { redemption: TenantRewardRedemption; selectedLocationName: string | undefined; busy: boolean; fulfill: () => void; reset: () => void; validationOnly?: boolean }) {
  const canFulfill = redemption.valid === true && redemption.status.toLowerCase() === "issued";
  const redeemed = redemption.status.toLowerCase() === "fulfilled";
  const checkout = redemptionCheckoutSummary(redemption);
  const isMonetaryDiscount = ["PERCENTAGE_DISCOUNT", "FIXED_DISCOUNT"].includes(redemption.rewardType ?? "");
  return <article className={`redemption-validation ${redeemed ? "is-redeemed" : redemption.valid ? "is-valid" : "is-invalid"}`} aria-live="polite">
    <div className="redemption-validation__heading">{redeemed || redemption.valid ? <CheckCircle2 /> : <AlertTriangle />}<div><small>{redeemed ? "Redemption complete" : redemption.valid ? (validationOnly ? "Valid coupon" : "Valid coupon · ready to redeem") : "Action needed"}</small><h3>{redemption.rewardName}</h3></div><StatusBadge status={redemptionStatus(redemption.status)} /></div>
    <p>{redeemed ? "This coupon is now Redeemed and cannot be used again." : redemption.validationMessage ?? (redemption.valid ? (validationOnly ? "This coupon is authentic, belongs to this business, and remains available for a member checkout." : "The coupon belongs to this business and every live condition passed.") : "Complete the highlighted checkout information, then validate again.")}</p>
    <dl><div><dt>Customer</dt><dd>{redemption.customerName}</dd></div><div><dt>Coupon</dt><dd>{checkout.benefitLabel}</dd></div><div><dt>Location</dt><dd>{redemption.locationName ?? selectedLocationName ?? (redemption.locationRequired ? "Select an eligible location" : "All eligible locations")}</dd></div><div><dt>Purchase</dt><dd>{redemption.purchaseReference ?? "Not supplied"}</dd></div><div><dt>Points used when issued</dt><dd>{formatNumber(redemption.pointsSpent)}</dd></div><div><dt>Points deducted now</dt><dd>0</dd></div></dl>
    {(checkout.purchaseAmountMinor !== null || checkout.appliedValueMinor !== null) && <section className="redemption-payment-summary" aria-label="Coupon checkout calculation">
      <div><span>Purchase total</span><strong>{checkout.purchaseAmountMinor === null ? "Not required" : formatMoney(checkout.purchaseAmountMinor, redemption.currencyCode)}</strong></div>
      <div><span>Coupon applies</span><strong>{isMonetaryDiscount && checkout.appliedValueMinor !== null ? `− ${formatMoney(checkout.appliedValueMinor, redemption.currencyCode)}` : checkout.benefitLabel}</strong></div>
      <div className="redemption-payment-summary__due"><span>Customer pays</span><strong>{checkout.customerPaysMinor === null ? (redemption.rewardType === "FREE_ITEM" ? "No charge for eligible item" : "Calculated at checkout") : formatMoney(checkout.customerPaysMinor, redemption.currencyCode)}</strong></div>
    </section>}
    {redemption.rewardType === "FREE_ITEM" && <p className="redemption-zero-charge"><Coins /> No points or money are deducted for the eligible free item at redemption.</p>}
    {redemption.termsText && <small className="redemption-validation__terms">{redemption.termsText}</small>}
    {redemption.mechanismTerms?.map((term) => <small className="redemption-validation__terms" key={term}>{term}</small>)}
    {canFulfill && !validationOnly && <button className="button button--primary button--full" disabled={busy} type="button" onClick={fulfill}><PackageCheck /> {busy ? "Redeeming…" : "Redeem coupon"}</button>}
    {canFulfill && validationOnly && <button className="button button--outline button--full" type="button" onClick={reset}><CheckCircle2 /> Validation complete</button>}
    {redeemed && <button className="button button--outline button--full" type="button" onClick={reset}><Plus /> Start next redemption</button>}
  </article>;
}

const newManualIssueKey = () => crypto.randomUUID();

export function ManualRewardIssuancePanel({
  locations,
  rewards,
  canSearchCustomers = true,
  canIssueApprovalRequired = false,
}: {
  locations: CheckoutLocation[];
  rewards: Reward[];
  canSearchCustomers?: boolean;
  canIssueApprovalRequired?: boolean;
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [customerPublicId, setCustomerPublicId] = useState("");
  const [rewardPublicId, setRewardPublicId] = useState("");
  const [locationPublicId, setLocationPublicId] = useState("");
  const [reason, setReason] = useState("");
  const [idempotencyKey, setIdempotencyKey] = useState(newManualIssueKey);
  const [result, setResult] = useState<TenantManualRewardIssueResult | null>(null);
  const [error, setError] = useState("");
  const customersQuery = useQuery({
    queryKey: ["tenant-customer-search", search.trim()],
    queryFn: () => fetchTenantCustomers({
      ...(search.trim() ? { q: search.trim() } : {}),
      limit: 25,
      offset: 0,
    }),
    staleTime: 30_000,
    enabled: canSearchCustomers,
  });
  const eligibleRewards = rewards.filter((reward) => reward.lifecycleStatus === "active"
    && reward.inventoryRemaining > 0
    && (!reward.requiresApproval || canIssueApprovalRequired)
    && !["BONUS_POINTS", "POINTS_MULTIPLIER"].includes(reward.rewardType));
  const selectedReward = eligibleRewards.find((reward) => reward.publicId === rewardPublicId);
  const eligibleLocations = selectedReward?.eligibleLocationPublicIds.length
    ? locations.filter((location) => selectedReward.eligibleLocationPublicIds.includes(location.publicId))
    : locations;
  const selectedCustomer = customersQuery.data?.find((customer) => customer.customerPublicId === customerPublicId);
  const mutation = useMutation({
    mutationFn: () => issueTenantReward(rewardPublicId, {
      customerPublicId,
      locationPublicId,
      reason: reason.trim(),
      idempotencyKey,
    }),
    onSuccess: async (issued) => {
      setResult(issued);
      setError("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: redemptionsKey }),
        queryClient.invalidateQueries({ queryKey: dashboardKey }),
        queryClient.invalidateQueries({ queryKey: auditKey }),
      ]);
    },
    onError: (caught) => { setError(messageFromError(caught)); },
  });
  const submit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    if (!customerPublicId) { setError("Choose a customer before issuing the reward."); return; }
    if (!rewardPublicId) { setError("Choose an active reward before issuing it."); return; }
    if (!locationPublicId) { setError("Choose an authorized, reward-eligible location before issuing the reward."); return; }
    if (reason.trim().length < 2) { setError("Record a reason with at least two characters."); return; }
    mutation.mutate();
  };
  const reset = () => {
    setResult(null);
    setRewardPublicId("");
    setLocationPublicId("");
    setReason("");
    setIdempotencyKey(newManualIssueKey());
    setError("");
  };

  return <section className="portal-panel reward-management-panel manual-issuance-panel">
    <div className="panel-heading"><div><p className="eyebrow">Service recovery & recognition</p><h2>Issue a reward</h2><p>Grant one active reward to a Business Owner customer with a recorded reason and an auditable one-time credential.</p></div><Gift /></div>
    {error && <AppAlert message={error} onDismiss={() => { setError(""); }} />}
    {result ? <div className="manual-issuance-result" aria-live="polite">
      <div className="manual-issuance-result__summary"><CheckCircle2 /><div><small>{result.replayed ? "Existing issuance found" : "Reward issued"}</small><h3>{result.issuance.reward.name}</h3><p>{selectedCustomer ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}` : "Selected customer"} · {formatMoney(result.issuance.valueMinor)}</p></div><StatusBadge status={result.issuance.status} /></div>
      {result.token ? <div className="manual-issuance-credential">
        <QRCodeSVG value={result.token} size={164} level="M" bgColor="#F7F5F0" fgColor="#122937" aria-label="One-time reward redemption QR code" />
        <div><strong>One-time QR ready</strong><p>Scan it now or let the customer open the reward from their wallet. Mero Perks stores only its secure hash and rejects reuse.</p></div>
      </div> : <div className="manual-issuance-replay" role="status"><ShieldCheck /><p>This request was already completed. No duplicate reward was created, and the one-time credential cannot be displayed again.</p></div>}
      <div className="management-form-actions"><button className="button button--primary" type="button" onClick={reset}>Issue another reward</button></div>
    </div> : <form className="manual-issuance-form" onSubmit={submit}>
      <div className="management-form-grid">
        {canSearchCustomers ? <><label className="management-field--wide"><span>Find customer</span><input value={search} onChange={(event) => { setSearch(event.target.value); setCustomerPublicId(""); }} placeholder="Search by name, email, or loyalty number" /></label><label><span>Customer</span><select required disabled={customersQuery.isLoading || customersQuery.isError} value={customerPublicId} onChange={(event) => { setCustomerPublicId(event.target.value); }}><option value="">{customersQuery.isLoading ? "Loading customers…" : customersQuery.isError ? "Customer search unavailable" : "Choose a customer"}</option>{customersQuery.data?.map((customer) => <option key={customer.customerPublicId} value={customer.customerPublicId}>{customer.firstName} {customer.lastName} · {customer.loyaltyNumber} · {customer.email}</option>)}</select></label></> : <label className="management-field--wide"><span>Customer public ID</span><input required value={customerPublicId} onChange={(event) => { setCustomerPublicId(event.target.value.trim()); }} placeholder="Paste the Business Owner customer public ID" /><small>Customer search requires customer.read. The server still verifies this customer belongs to your Business Owner account.</small></label>}
        <label><span>Active reward</span><select required value={rewardPublicId} onChange={(event) => { setRewardPublicId(event.target.value); setLocationPublicId(""); }}><option value="">Choose a reward</option>{eligibleRewards.map((reward) => <option key={reward.publicId} value={reward.publicId}>{reward.title} · {reward.inventoryRemaining} available</option>)}</select><small>Transaction-triggered points benefits are awarded automatically. Approval-required rewards are available only to approvers.</small></label>
        <label><span>Issuing location</span><select required disabled={!rewardPublicId || eligibleLocations.length === 0} value={locationPublicId} onChange={(event) => { setLocationPublicId(event.target.value); }}><option value="">{!rewardPublicId ? "Choose a reward first" : eligibleLocations.length ? "Choose an authorized location" : "No authorized eligible location"}</option>{eligibleLocations.map((location) => <option key={location.publicId} value={location.publicId}>{location.name}</option>)}</select><small>Only locations assigned to you and eligible for this reward are shown.</small></label>
        <label className="management-field--wide"><span>Reason for issuance</span><textarea required minLength={2} maxLength={1000} rows={3} value={reason} onChange={(event) => { setReason(event.target.value); }} placeholder="For example: Service recovery approved after delayed order." /></label>
      </div>
      {canSearchCustomers && customersQuery.isError && <AppAlert message={messageFromError(customersQuery.error)} />}
      {!eligibleRewards.length && <p className="manual-issuance-replay"><AlertTriangle /> Activate a reward with available inventory before issuing it manually.</p>}
      {!locations.length && <p className="manual-issuance-replay"><AlertTriangle /> No authorized active location is available for manual issuance.</p>}
      <div className="management-form-actions"><button className="button button--primary" disabled={mutation.isPending || !eligibleRewards.length || !locations.length} type="submit">{mutation.isPending ? "Issuing…" : "Issue reward securely"}</button></div>
    </form>}
  </section>;
}

function CouponScannerDialog({ close, scanned }: { close: () => void; scanned: (value: string) => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const controls = useRef<IScannerControls | null>(null);
  const accepted = useRef(false);
  const scannedRef = useRef(scanned);
  const [state, setState] = useState<"starting" | "active" | "denied" | "error">("starting");
  const [message, setMessage] = useState("");
  useEffect(() => {
    let mounted = true;
    const start = async () => {
      try {
        const { BrowserQRCodeReader } = await import("@zxing/browser");
        if (!video.current) throw new Error("The camera preview could not be opened.");
        const reader = new BrowserQRCodeReader(undefined, { delayBetweenScanAttempts: 200 });
        controls.current = await reader.decodeFromConstraints(rearCameraConstraints, video.current, (result) => {
          const value = result?.getText().trim();
          if (!value || accepted.current) return;
          accepted.current = true;
          controls.current?.stop();
          scannedRef.current(value);
        });
        if (mounted) setState("active");
      } catch (error) {
        if (!mounted) return;
        const failure = describeCameraFailure(error, "Check camera permission and ask the customer to refresh the QR.");
        setState(failure.state);
        setMessage(failure.message);
      }
    };
    void start();
    return () => {
      mounted = false;
      controls.current?.stop();
      controls.current = null;
    };
  }, []);
  return <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="coupon-scanner-title">
    <section className="modal tenant-coupon-scanner">
      <button className="modal__close" type="button" aria-label="Close coupon scanner" onClick={close}><X /></button>
      <p className="eyebrow">COUPON SCANNER</p>
      <h2 id="coupon-scanner-title">Scan the customer reward QR</h2>
      <p className="modal-intro">The short-lived QR is accepted once. Staff must still validate the conditions and confirm fulfillment.</p>
      <div className="tenant-coupon-scanner__camera">
        <video ref={video} muted playsInline aria-label="Live camera preview for a reward QR code" />
        <div className="tenant-coupon-scanner__guide"><span /><span /><ScanLine /></div>
        {state !== "active" && <div className="tenant-coupon-scanner__state">
          {state === "starting" ? <LoaderCircle className="spinner" /> : <VideoOff />}
          <strong>{state === "starting" ? "Starting camera…" : "Camera unavailable"}</strong>
        </div>}
      </div>
      {message && <AppAlert message={message} tone="warning" />}
      <button className="button button--outline button--full" type="button" onClick={close}>Close scanner</button>
    </section>
  </div>;
}

export function RewardRedemptionsView({ locations, rewards, canIssueManual, canIssueApprovalRequired, canReadCustomers, canRedeem, canReverse, compact = false, validationOnly = false }: { locations: CheckoutLocation[]; rewards: Reward[]; canIssueManual: boolean; canIssueApprovalRequired: boolean; canReadCustomers: boolean; canRedeem: boolean; canReverse: boolean; compact?: boolean; validationOnly?: boolean }) {
  const queryClient = useQueryClient();
  const redemptionsQuery = useQuery({ queryKey: redemptionsKey, queryFn: fetchTenantRewardRedemptions, enabled: canRedeem });
  const [credential, setCredential] = useState("");
  const [manualCode, setManualCode] = useState("");
  const [locationPublicId, setLocationPublicId] = useState("");
  const [amount, setAmount] = useState("");
  const [purchaseReference, setPurchaseReference] = useState("");
  const [channel, setChannel] = useState("IN_STORE");
  const [productCode, setProductCode] = useState("");
  const [categoryCode, setCategoryCode] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [mechanismSelectionId, setMechanismSelectionId] = useState("");
  const [checked, setChecked] = useState<TenantRewardRedemption | null>(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!locationPublicId && locations.length === 1) setLocationPublicId(locations[0]?.publicId ?? "");
  }, [locationPublicId, locations]);
  const refresh = async () => {
    await Promise.all([queryClient.invalidateQueries({ queryKey: redemptionsKey }), queryClient.invalidateQueries({ queryKey: dashboardKey }), queryClient.invalidateQueries({ queryKey: auditKey })]);
  };
  const validateMutation = useMutation({
    mutationFn: (presentedCredential: string) => validateTenantRewardRedemption({
      redemptionCode: presentedCredential.trim(),
      ...redemptionRequest(locationPublicId, amount, purchaseReference, channel, productCode, categoryCode, quantity, mechanismSelectionId),
    }),
    onSuccess: (redemption) => { setChecked(redemption); setError(""); },
    onError: (caught) => { setChecked(null); setError(messageFromError(caught)); },
  });
  const fulfillMutation = useMutation({
    mutationFn: (redemption: TenantRewardRedemption) => fulfillTenantRewardRedemption(
      redemption.publicId,
      redemptionRequest(locationPublicId, amount, purchaseReference, channel, productCode, categoryCode, quantity, mechanismSelectionId),
    ),
    onSuccess: async (redemption) => { setChecked(redemption); setCredential(""); setManualCode(""); setError(""); await refresh(); },
    onError: (caught) => { setError(messageFromError(caught)); },
  });
  const reverseMutation = useMutation({
    mutationFn: ({ redemption, reason }: { redemption: TenantRewardRedemption; reason: string }) => reverseTenantRewardRedemption(redemption.publicId, reason),
    onSuccess: refresh,
    onError: (caught) => { setError(messageFromError(caught)); },
  });
  const reset = () => {
    setCredential("");
    setManualCode("");
    setAmount("");
    setPurchaseReference("");
    setProductCode("");
    setCategoryCode("");
    setQuantity("1");
    setMechanismSelectionId("");
    setChecked(null);
    setError("");
  };
  const validateCredential = (value: string) => {
    if (!value.trim()) return;
    setCredential(value.trim());
    setError("");
    validateMutation.mutate(value.trim());
  };
  const submit = (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    validateCredential(manualCode);
  };
  const locked = checked?.valid === true && checked.status.toLowerCase() === "issued";
  return <>{canRedeem && <div className={`redemption-workspace${compact ? " redemption-workspace--checkout" : ""}`}>
    <section className="portal-panel reward-management-panel redemption-terminal">
      <div className="panel-heading"><div><p className="eyebrow">{validationOnly ? "Coupon validation" : "Coupon redemption"}</p><h2>Scan or enter a coupon</h2><p>{validationOnly ? "Check authenticity and live conditions without redeeming the coupon." : "Verify the issuing business and live conditions, then apply the benefit and mark it Redeemed."}</p></div><TicketCheck /></div>
      {error && <AppAlert message={error} onDismiss={() => { setError(""); }} />}
      <form className="redemption-check-form" onSubmit={submit}>
        <div className="redemption-entry-options">
          <section className="redemption-entry-option"><span className="redemption-entry-option__icon"><Camera /></span><div><strong>Scan customer QR</strong><small>Uses the phone or counter camera.</small></div><button className="button button--primary" disabled={locked || validateMutation.isPending} type="button" onClick={() => { setScannerOpen(true); }}><Camera /> Scan QR</button></section>
          <section className="redemption-entry-option"><span className="redemption-entry-option__icon"><TicketCheck /></span><div><strong>Enter coupon code</strong><small>Use the short code shown below the customer’s QR.</small></div><div className="redemption-manual-entry"><label><span className="sr-only">One-time coupon code</span><input disabled={locked} minLength={4} maxLength={32} autoCapitalize="characters" autoComplete="off" value={manualCode} onChange={(event) => { setManualCode(event.target.value.toUpperCase()); setCredential(""); setChecked(null); }} placeholder="XXXX-XXXX-XXXX" /></label><button className="button button--outline" disabled={locked || validateMutation.isPending || manualCode.replace(/[\s-]/g, "").length < 4} type="submit">Check code</button></div></section>
        </div>
        {(credential || checked) && <div className="redemption-captured" role="status"><ShieldCheck /><div><strong>{checked ? checked.rewardName : "Coupon captured"}</strong><small>{locked ? "One-time credential accepted and locked to this redemption." : "Complete any required checkout details, then validate again."}</small></div>{!locked && credential && <button className="button button--primary button--compact" disabled={validateMutation.isPending} type="button" onClick={() => { validateCredential(credential); }}>{validateMutation.isPending ? "Checking…" : "Validate coupon"}</button>}</div>}
        <fieldset className="redemption-purchase-fields" disabled={locked}>
          <legend>Checkout details</legend>
          <label><span>Location <small>(required for location-restricted coupons)</small></span><select value={locationPublicId} onChange={(event) => { setLocationPublicId(event.target.value); setChecked(null); }}><option value="">Choose redemption location</option>{locations.map((location) => <option key={location.publicId} value={location.publicId}>{location.name}</option>)}</select></label>
          <label><span>Purchase total (NPR) <small>(required for discounts)</small></span><input min="0" step="0.01" inputMode="decimal" type="number" value={amount} onChange={(event) => { setAmount(event.target.value); setChecked(null); }} placeholder="0.00" /></label>
          <label><span>Receipt / order reference <small>(recommended)</small></span><input maxLength={128} autoComplete="off" value={purchaseReference} onChange={(event) => { setPurchaseReference(event.target.value); setChecked(null); }} placeholder="Receipt or order number" /></label>
          <label><span>Channel</span><select value={channel} onChange={(event) => { setChannel(event.target.value); setChecked(null); }}><option value="IN_STORE">In store</option><option value="ONLINE">Online</option><option value="POS_API">POS / API</option><option value="HOTEL_DESK">Hotel desk</option><option value="CAFE_COUNTER">Cafe counter</option><option value="MOBILE_APP">Mobile app</option></select></label>
          <details className="redemption-advanced-fields"><summary>Product or choice details</summary><div><label><span>Product code <small>(if scoped)</small></span><input value={productCode} onChange={(event) => { setProductCode(event.target.value); setChecked(null); }} placeholder="SKU-123" /></label><label><span>Category code <small>(if scoped)</small></span><input value={categoryCode} onChange={(event) => { setCategoryCode(event.target.value); setChecked(null); }} placeholder="COFFEE" /></label><label><span>Qualifying quantity</span><input min="1" step="1" type="number" value={quantity} onChange={(event) => { setQuantity(event.target.value); setChecked(null); }} /></label><label><span>Choice option ID <small>(for choice rewards)</small></span><input value={mechanismSelectionId} onChange={(event) => { setMechanismSelectionId(event.target.value); setChecked(null); }} placeholder="Published option ID" /></label></div></details>
        </fieldset>
      </form>
      {checked && <ValidationResult redemption={checked} selectedLocationName={locations.find((location) => location.publicId === locationPublicId)?.name} busy={fulfillMutation.isPending} fulfill={() => { fulfillMutation.mutate(checked); }} reset={reset} validationOnly={validationOnly} />}
    </section>

    {!compact && <section className="portal-panel reward-management-panel redemption-history-panel">
      <div className="panel-heading"><div><p className="eyebrow">Live register</p><h2>Recent redemptions</h2><p>Issued and Redeemed coupons are tracked here. Private QR payloads and manual codes are never displayed.</p></div><button className="button button--quiet button--compact" type="button" onClick={() => { void redemptionsQuery.refetch(); }}>Refresh</button></div>
      {redemptionsQuery.isLoading ? <LoadingState label="Loading redemptions" /> : redemptionsQuery.isError ? <ErrorState error={redemptionsQuery.error} retry={() => { void redemptionsQuery.refetch(); }} /> : redemptionsQuery.data?.length ? <div className="management-table-wrap"><table className="management-table"><thead><tr><th>Reward & member</th><th>Points</th><th>Value</th><th>Location / date</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead><tbody>{redemptionsQuery.data.map((redemption) => <tr key={redemption.publicId}>
        <td><strong>{redemption.rewardName}</strong><small><UserRound /> {redemption.customerName}</small></td>
        <td data-label="Points"><strong>{formatNumber(redemption.pointsSpent)}</strong><small>points spent</small></td>
        <td data-label="Value"><strong>{formatMoney(redemption.valueMinor)}</strong></td>
        <td data-label="Location / date"><strong>{redemption.locationName ?? (redemption.status.toLowerCase() === "fulfilled" ? "Location not recorded" : "Not redeemed")}</strong><small>{formatDate(redemption.fulfilledAt ?? redemption.createdAt, true)}</small></td>
        <td data-label="Status"><StatusBadge status={redemptionStatus(redemption.status)} /></td>
        <td>{canReverse && ["issued", "fulfilled"].includes(redemption.status.toLowerCase()) && !redemption.reversedAt && <button className="table-action table-action--danger" disabled={reverseMutation.isPending} type="button" onClick={() => { const reason = window.prompt("Why is this redemption being reversed?"); if (reason?.trim() && reason.trim().length >= 2) reverseMutation.mutate({ redemption, reason: reason.trim() }); }}><RotateCcw /> Reverse</button>}</td>
      </tr>)}</tbody></table></div> : <EmptyState icon={<TicketCheck />} title="No redemptions yet" detail="Issued customer rewards will appear here." />}
    </section>}
  </div>}{canIssueManual && !compact && <details className="manual-issuance-disclosure"><summary><Gift /> Issue a reward to a customer</summary><ManualRewardIssuancePanel locations={locations} rewards={rewards} canSearchCustomers={canReadCustomers} canIssueApprovalRequired={canIssueApprovalRequired} /></details>}{scannerOpen && <CouponScannerDialog close={() => { setScannerOpen(false); }} scanned={(value) => { setCredential(value); setManualCode(""); setChecked(null); setError(""); setScannerOpen(false); validateCredential(value); }} />}</>;
}

const summarizeChange = (value: unknown) => {
  if (value === null || value === undefined) return "No previous value";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  if (typeof value !== "object") return "Recorded value";
  const keys = Object.keys(value);
  return keys.length ? `${keys.slice(0, 4).map(titleCase).join(", ")}${keys.length > 4 ? ` +${String(keys.length - 4)} more` : ""}` : "Empty object";
};

export function RewardGovernanceView({ canViewAnalytics, canViewAudit }: { canViewAnalytics: boolean; canViewAudit: boolean }) {
  const auditQuery = useQuery({ queryKey: auditKey, queryFn: fetchTenantRewardAudit, enabled: canViewAudit });
  const dashboardQuery = useQuery({ queryKey: dashboardKey, queryFn: fetchTenantRewardDashboard, enabled: canViewAnalytics });
  const summary = dashboardQuery.data?.summary;
  const exposure = summary?.totalRewardExposureMinor || (summary?.committedMinor ?? 0) + (summary?.redeemedValueMinor ?? 0);
  const budget = summary?.configuredBudgetMinor ?? 0;
  const utilization = budget > 0 ? Math.min(100, (exposure / budget) * 100) : 0;
  return <div className="governance-workspace">
    {canViewAnalytics && <section className="portal-panel reward-management-panel governance-summary">
      <div className="panel-heading"><div><p className="eyebrow">Financial controls</p><h2>Reward liability</h2><p>Business Owner-funded reward exposure and Business Reward Point balances from the live strategy dashboard.</p></div><ShieldCheck /></div>
      {dashboardQuery.isLoading ? <LoadingState label="Loading liability" /> : dashboardQuery.isError ? <ErrorState error={dashboardQuery.error} retry={() => { void dashboardQuery.refetch(); }} /> : <><div className="liability-grid">
        <article><span><CircleDollarSign /></span><small>Configured budgets</small><strong>{formatMoney(budget)}</strong><em>Across reward budgets</em></article>
        <article><span><WalletCards /></span><small>Committed liability</small><strong>{formatMoney(summary?.committedMinor ?? 0)}</strong><em>Reserved for issued rewards</em></article>
        <article><span><PackageCheck /></span><small>Redeemed value</small><strong>{formatMoney(summary?.redeemedValueMinor ?? 0)}</strong><em>Benefit already fulfilled</em></article>
        <article><span><Coins /></span><small>Outstanding points</small><strong>{formatNumber(summary?.outstandingBusinessPoints ?? 0)}</strong><em>Business Reward Points</em></article>
      </div>
      <div className="liability-utilization"><div><span>Budget exposure</span><strong>{formatMoney(exposure)}{budget > 0 ? ` of ${formatMoney(budget)}` : " · no aggregate cap"}</strong></div><i><b style={{ width: `${String(utilization)}%` }} /></i><small>{budget > 0 ? `${utilization.toFixed(1)}% utilized or committed` : "Set reward and campaign budgets to define a ceiling."}</small></div></>}
    </section>}

    {canViewAudit && <section className="portal-panel reward-management-panel audit-panel">
      <div className="panel-heading"><div><p className="eyebrow">Governance record</p><h2>Audit trail</h2><p>Immutable reward, campaign, automation, and redemption events recorded by the platform.</p></div><button className="button button--quiet button--compact" type="button" onClick={() => { void auditQuery.refetch(); }}>Refresh</button></div>
      {auditQuery.isLoading ? <LoadingState label="Loading audit trail" /> : auditQuery.isError ? <ErrorState error={auditQuery.error} retry={() => { void auditQuery.refetch(); }} /> : auditQuery.data?.length ? <div className="audit-timeline">{auditQuery.data.map((event) => <article key={event.publicId}>
        <span><History /></span>
        <div><div className="audit-timeline__heading"><h3>{titleCase(event.action.replaceAll(".", "_"))}</h3><time dateTime={event.createdAt ?? undefined}>{formatDate(event.createdAt, true)}</time></div><p>{event.actorName ?? "System process"} · {titleCase(event.entityType)} <code>{event.entityId.slice(0, 12)}{event.entityId.length > 12 ? "…" : ""}</code></p><details><summary>View recorded changes</summary><div className="audit-change-grid"><section><strong>Before</strong><small>{summarizeChange(event.before)}</small><pre>{JSON.stringify(event.before, null, 2)}</pre></section><section><strong>After</strong><small>{summarizeChange(event.after)}</small><pre>{JSON.stringify(event.after, null, 2)}</pre></section></div></details></div>
      </article>)}</div> : <EmptyState icon={<History />} title="No audit events yet" detail="Governed changes will appear here as rewards and operations are created." />}
    </section>}
  </div>;
}
